import React, { useState, createContext, useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { formsService } from '../../services/formsService';
import { submissionsService } from '../../services/submissionsService';


// Create context for sidebar state
const SidebarContext = createContext({ isCollapsed: false });
const SHOW_IMPERSONATION_BANNER = false;

export const useSidebar = () => useContext(SidebarContext);

// Export SidebarContext for use in other components
export { SidebarContext };

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { user, isAdmin, isOwner } = useAuth();
  const { hasFeatureAccess } = usePlanLimits();
  const [forms, setForms] = useState([]);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [openGroups, setOpenGroups] = useState({});
  const [impersonationData, setImpersonationData] = useState(null);
  const [returningToAdmin, setReturningToAdmin] = useState(false);

  const isSuperAdmin = isAdmin || isOwner || (user?.email && user?.email === import.meta.env?.VITE_SUPER_ADMIN_EMAIL);

  // When impersonating, show the regular user view (not super admin nav)
  const effectiveIsSuperAdmin = isSuperAdmin && !impersonationData;

  // Check if we're in impersonation mode
  useEffect(() => {
    const stored = sessionStorage?.getItem('superAdminImpersonating');
    if (stored) {
      try {
        setImpersonationData(JSON.parse(stored));
      } catch {
        sessionStorage?.removeItem('superAdminImpersonating');
      }
    }
  }, [user]);

  const handleReturnToAdmin = async () => {
    if (!impersonationData) return;
    setReturningToAdmin(true);
    try {
      // Simply clear impersonation data — admin session was never changed
      sessionStorage?.removeItem('superAdminImpersonating');
      setImpersonationData(null);
      navigate('/super-admin-dashboard');
    } catch (err) {
      sessionStorage?.removeItem('superAdminImpersonating');
      setImpersonationData(null);
      navigate('/super-admin-dashboard');
    } finally {
      setReturningToAdmin(false);
    }
  };

  // Load forms and submissions data
  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      try {
        const [formsData, submissionsData] = await Promise.all([
          formsService?.getForms(),
          submissionsService?.getAllSubmissions()
        ]);
        setForms(formsData || []);
        setTotalSubmissions(submissionsData?.length || 0);
      } catch (err) {
        console.error('Error loading sidebar data:', err);
      }
    };

    loadData();
  }, [user]);

  const planData = {
    planName: effectiveIsSuperAdmin ? 'Owner' : 'Professional',
    billingCycle: 'Jan 15 - Feb 15, 2026',
    nextBillingDate: 'February 15, 2026',
    monthlyPrice: 49,
    limits: [
      {
        id: 1,
        icon: 'FileText',
        label: 'Forms',
        used: forms?.length || 0,
        limit: 50
      },
      {
        id: 2,
        icon: 'Send',
        label: 'Monthly Submissions',
        used: totalSubmissions,
        limit: 5000
      }
    ]
  };

  const getUsagePercentage = (used, limit) => {
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-error';
    if (percentage >= 75) return 'bg-warning';
    return 'bg-primary';
  };

  // Update CSS custom property when sidebar state changes
  useEffect(() => {
    if (isCollapsed) {
      document.documentElement?.style?.setProperty('--sidebar-width', '64px');
    } else {
      document.documentElement?.style?.setProperty('--sidebar-width', '256px');
    }
  }, [isCollapsed]);

  // Auto-collapse sidebar on form-builder pages
  useEffect(() => {
    const isFormBuilderPage = location?.pathname?.includes('form-builder');
    if (isFormBuilderPage) {
      setIsCollapsed(true);
    } else {
      setIsCollapsed(false);
    }
  }, [location?.pathname]);

  const groupedNavigation = [
    {
      groupLabel: 'Activity & Analytics',
      icon: 'BarChart2',
      items: [
        { label: 'Submissions', path: '/submissions-management', icon: 'Inbox' },
        { label: 'Analytics', path: '/analytics', icon: 'BarChart3' },
      ]
    },
    {
      groupLabel: 'Publishing',
      icon: 'Globe',
      items: [
        { label: 'Embed', path: '/embed-settings', icon: 'Code2' },
        { label: 'Publish', path: '/publish-screen', icon: 'Globe' },
        { label: 'API Docs', path: '/api-reference', icon: 'BookOpen' },
        ...(hasFeatureAccess(undefined, 'client_workspaces') ? [{ label: 'Client Workspaces', path: '/client-workspaces', icon: 'Users' }] : []),
      ]
    },
    ...(hasFeatureAccess(undefined, 'white_label') ? [{
      groupLabel: 'White Label',
      icon: 'Building2',
      items: [
        { label: 'Branding', path: '/white-label-configuration?tab=branding', icon: 'Palette' },
        { label: 'Custom Domain', path: '/white-label-configuration?tab=domain', icon: 'Globe' },
        { label: 'Embed Styling', path: '/white-label-configuration?tab=embed', icon: 'Code2' },
        { label: 'Customer Customization', path: '/white-label-configuration?tab=customer', icon: 'UserCog' },
        { label: 'Customer Accounts', path: '/white-label-accounts', icon: 'Users' },
        { label: 'Customer Portal', path: '/white-label-customer-portal', icon: 'LayoutDashboard' },
        { label: 'Submissions', path: '/portal-submissions-dashboard', icon: 'Inbox' },
        { label: 'Analytics', path: '/portal-analytics-dashboard', icon: 'BarChart2' },
        { label: 'Preview & Test', path: '/white-label-preview', icon: 'Eye' },
      ]
    }] : []),
  ];

  // Super Admin quick navigation items
  const superAdminQuickNav = [
    { label: 'Super Admin Dashboard', path: '/super-admin-dashboard', icon: 'ShieldCheck' },
    { label: 'Client Accounts', path: '/account-management-console', icon: 'Users' },
    { label: 'Pricing Plans', path: '/pricing-plans-management', icon: 'Tag' },
    { label: 'Subscription Management', path: '/subscription-management-dashboard', icon: 'CreditCard' },
    { label: 'User Analytics', path: '/user-analytics-dashboard', icon: 'BarChart3' },
    { label: 'A2P Consents', path: '/a2p-consent-management', icon: 'ShieldCheck' },
    { label: 'Payment Processors', path: '/payment-processors-management', icon: 'Zap' },
    { label: 'White-Label', path: '/white-label-branding', icon: 'Building2' },
    { label: 'Test Users', path: '/test-user-management', icon: 'FlaskConical' },
  ];

  // Initialize all groups as closed
  useEffect(() => {
    const initial = {};
    groupedNavigation?.forEach((g) => {
      initial[g.groupLabel] = false;
    });
    setOpenGroups(initial);
  }, []);

  const toggleGroup = (label) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev?.[label] }));
  };

  const bottomNavItems = [
    {
      label: 'Super Admin',
      path: '/super-admin-login',
      icon: 'ShieldCheck'
    },
    {
      label: 'Help & Support',
      path: '/help-center-hub',
      icon: 'HelpCircle'
    }
  ];

  const isActive = (path) => {
    const [pathOnly, queryString] = path.split('?');
    if (queryString) {
      const params = new URLSearchParams(queryString);
      const tabParam = params.get('tab');
      const currentTab = new URLSearchParams(location?.search).get('tab');
      return location?.pathname === pathOnly && currentTab === tabParam;
    }
    if (path === '/templates-gallery') {
      return location?.pathname === path || location?.pathname?.startsWith('/templates/');
    }
    return location?.pathname === path;
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const tourAttrMap = {
    '/templates-gallery': 'sidebar-templates',
    '/analytics': 'sidebar-analytics',
    '/submissions-management': 'sidebar-submissions',
    '/embed-settings': 'sidebar-embed',
  };

  return (
    <SidebarContext.Provider value={{ isCollapsed }}>
      {/* Sidebar */}
      <aside
        className={`
          fixed top-16 left-0 bottom-0 bg-card border-r border-border z-[999]
          transition-all duration-300 ease-in-out
          ${isCollapsed ? 'w-16' : 'w-64'}
        `}
      >
        {/* Toggle Button */}
        <button
          onClick={toggleSidebar}
          className="absolute -right-3 top-6 w-6 h-6 bg-card border border-border rounded-full flex items-center justify-center hover:bg-muted transition-smooth shadow-sm"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon
            name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'}
            size={14}
            color="var(--color-foreground)"
          />
        </button>

        {/* Navigation Items */}
        <nav className="h-full overflow-y-auto scrollbar-custom py-4">
          {/* Impersonation Banner */}
          {SHOW_IMPERSONATION_BANNER && impersonationData && (
            <div className={`mx-3 mb-3 rounded-lg border border-amber-300 bg-amber-50 overflow-hidden ${isCollapsed ? 'px-1 py-2' : 'p-3'}`}>
              {!isCollapsed ? (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="AlertTriangle" size={14} className="text-amber-600 flex-shrink-0" />
                    <span className="text-xs font-semibold text-amber-800">Viewing as User</span>
                  </div>
                  <p className="text-xs text-amber-700 mb-2 truncate">{impersonationData?.targetEmail}</p>
                  <button
                    onClick={handleReturnToAdmin}
                    disabled={returningToAdmin}
                    className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium transition-smooth disabled:opacity-60"
                  >
                    {returningToAdmin
                      ? <Icon name="Loader2" size={12} className="animate-spin" />
                      : <Icon name="ShieldCheck" size={12} />
                    }
                    Return to Admin
                  </button>
                </>
              ) : (
                <button
                  onClick={handleReturnToAdmin}
                  disabled={returningToAdmin}
                  title="Return to Admin"
                  className="w-full flex items-center justify-center p-1 rounded-md bg-amber-600 hover:bg-amber-700 text-white transition-smooth disabled:opacity-60"
                >
                  {returningToAdmin
                    ? <Icon name="Loader2" size={14} className="animate-spin" />
                    : <Icon name="ShieldCheck" size={14} />
                  }
                </button>
              )}
            </div>
          )}
          {/* Main Navigation Card */}
          {!isCollapsed ? (
            <div className="px-3 mb-4">
              <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
                {/* Card header label */}
                <div className="px-3 pt-3 pb-2 border-b border-border/60">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 select-none">
                    {effectiveIsSuperAdmin ? 'Quick Navigation' : 'Main Menu'}
                  </p>
                </div>

                {effectiveIsSuperAdmin ? (
                  /* Super Admin Quick Navigation */
                  (<div className="px-2 py-2 space-y-0.5">
                    {superAdminQuickNav?.map((item) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        className={`
                          flex items-center gap-3 px-3 py-2.5 rounded-md font-medium text-sm
                          transition-smooth group relative
                          ${isActive(item?.path)
                            ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }
                        `}
                      >
                        <Icon
                          name={item?.icon}
                          size={18}
                          color={isActive(item?.path) ? 'var(--color-primary)' : '#107570'}
                        />
                        <span className="flex-1">{item?.label}</span>
                        {isActive(item?.path) && (
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        )}
                      </Link>
                    ))}
                  </div>)
                ) : (
                  <>
                    {/* Dashboard */}
                    <div className="px-2 pt-2 pb-1">
                      <Link
                        to="/dashboard"
                        className={`
                          flex items-center gap-3 px-3 py-2.5 rounded-md font-medium text-sm
                          transition-smooth group relative
                          ${isActive('/dashboard')
                            ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }
                        `}
                      >
                        <Icon
                          name="LayoutDashboard"
                          size={18}
                          color={isActive('/dashboard') ? 'var(--color-primary)' : '#107570'}
                        />
                        <span className="flex-1">Dashboard</span>
                      </Link>
                    </div>

                    {/* Form Builder + Templates */}
                    <div className="px-2 pb-2 space-y-0.5">
                      <Link
                        to="/form-builder"
                        className={`
                          flex items-center gap-3 px-3 py-2.5 rounded-md font-medium text-sm
                          transition-smooth group relative
                          ${isActive('/form-builder')
                            ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }
                        `}
                      >
                        <Icon name="Wrench" size={18} color={isActive('/form-builder') ? 'var(--color-primary)' : '#107570'} />
                        <span className="flex-1">Form Builder</span>
                      </Link>
                      <Link
                        to="/templates-gallery"
                        className={`
                          flex items-center gap-3 px-3 py-2.5 rounded-md font-medium text-sm
                          transition-smooth group relative
                          ${isActive('/templates-gallery')
                            ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }
                        `}
                      >
                        <Icon name="LayoutTemplate" size={18} color={isActive('/templates-gallery') ? 'var(--color-primary)' : '#107570'} />
                        <span className="flex-1">Templates</span>
                      </Link>
                      <Link
                        to="/a-b-testing-dashboard"
                        data-tour="sidebar-ab-testing"
                        className={`
                          flex items-center gap-3 px-3 py-2.5 rounded-md font-medium text-sm
                          transition-smooth group relative
                          ${isActive('/a-b-testing-dashboard')
                            ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }
                        `}
                      >
                        <Icon name="TestTube2" size={18} color={isActive('/a-b-testing-dashboard') ? 'var(--color-primary)' : '#107570'} />
                        <span className="flex-1">A/B Testing</span>
                      </Link>
                    </div>

                    {/* Divider before grouped sections */}
                    <div className="mx-3 border-t border-border/60" />

                    {/* Grouped accordion sections */}
                    <div className="px-2 py-2 space-y-0.5">
                      {groupedNavigation?.map((group) => {
                        const isOpen = openGroups?.[group?.groupLabel] === true;
                        const hasActiveItem = group?.items?.some((item) => isActive(item?.path));
                        return (
                          <div key={group?.groupLabel}>
                            <button
                              onClick={() => toggleGroup(group?.groupLabel)}
                              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md transition-smooth group ${hasActiveItem ? 'bg-primary/5' : 'hover:bg-muted'}`}
                            >
                              <span className={`flex items-center gap-2.5 text-sm font-medium select-none ${hasActiveItem ? 'text-primary' : ''}`} style={hasActiveItem ? {} : { color: '#0e7572' }}>
                                <Icon
                                  name={group?.icon}
                                  size={18}
                                  color={hasActiveItem ? 'var(--color-primary)' : '#107570'}
                                />
                                {group?.groupLabel}
                              </span>
                              <Icon
                                name={isOpen ? 'ChevronDown' : 'ChevronRight'}
                                size={14}
                                color={hasActiveItem ? 'var(--color-primary)' : '#0e7572'}
                                className="transition-transform duration-200"
                              />
                            </button>
                            {isOpen && (
                              <div className="mt-0.5 mb-1 ml-2 pl-3 border-l-2 border-border space-y-0.5">
                                {group?.items?.map((item) => (
                                  <Link
                                    key={item?.path}
                                    to={item?.path}
                                    data-tour={tourAttrMap?.[item?.path] || undefined}
                                    className={`
                                      flex items-center gap-3 px-3 py-2 rounded-md font-medium text-sm
                                      transition-smooth group relative
                                      ${isActive(item?.path)
                                        ? 'bg-primary/10 text-primary' : 'text-[#6B7280] hover:text-foreground hover:bg-muted'
                                      }
                                    `}
                                  >
                                    <Icon
                                      name={item?.icon}
                                      size={16}
                                      color={isActive(item?.path) ? 'var(--color-primary)' : '#107570'}
                                    />
                                    <span className="flex-1">{item?.label}</span>
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            /* Collapsed state — keep original icon-only layout */
            (<>
              <div className="px-3 mb-4">
                {effectiveIsSuperAdmin ? (
                  /* Super Admin collapsed: show quick nav icons */
                  (<div className="space-y-0.5">
                    {superAdminQuickNav?.map((item) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        className={`
                          flex items-center justify-center px-3 py-2.5 rounded-md font-medium text-sm
                          transition-smooth group relative
                          ${isActive(item?.path)
                            ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }
                        `}
                        title={item?.label}
                      >
                        <Icon name={item?.icon} size={20} color={isActive(item?.path) ? 'var(--color-primary)' : '#107570'} />
                        <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">{item?.label}</div>
                      </Link>
                    ))}
                  </div>)
                ) : (
                  <Link
                    to="/dashboard"
                    className={`
                      flex items-center justify-center px-3 py-2.5 rounded-md font-medium text-sm
                      transition-smooth group relative
                      ${isActive('/dashboard')
                        ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }
                    `}
                    title="Dashboard"
                  >
                    <Icon name="LayoutDashboard" size={20} color={isActive('/dashboard') ? 'var(--color-primary)' : '#107570'} />
                    <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">Dashboard</div>
                  </Link>
                )}
              </div>
              {!effectiveIsSuperAdmin && (
                <>
                  <div className="px-3 mb-4 space-y-0.5">
                    <Link
                      to="/form-builder"
                      className={`flex items-center justify-center px-3 py-2.5 rounded-md font-medium text-sm transition-smooth group relative ${isActive('/form-builder') ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                      title="Form Builder"
                    >
                      <Icon name="Wrench" size={20} color={isActive('/form-builder') ? 'var(--color-primary)' : '#107570'} />
                      <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">Form Builder</div>
                    </Link>
                    <Link
                      to="/templates-gallery"
                      className={`flex items-center justify-center px-3 py-2.5 rounded-md font-medium text-sm transition-smooth group relative ${isActive('/templates-gallery') ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                      title="Templates"
                    >
                      <Icon name="LayoutTemplate" size={20} color={isActive('/templates-gallery') ? 'var(--color-primary)' : '#107570'} />
                      <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">Templates</div>
                    </Link>
                    <Link
                      to="/a-b-testing-dashboard"
                      data-tour="sidebar-ab-testing"
                      className={`flex items-center justify-center px-3 py-2.5 rounded-md font-medium text-sm transition-smooth group relative ${isActive('/a-b-testing-dashboard') ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                      title="A/B Testing"
                    >
                      <Icon name="TestTube2" size={20} color={isActive('/a-b-testing-dashboard') ? 'var(--color-primary)' : '#107570'} />
                      <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">A/B Testing</div>
                    </Link>
                  </div>
                  <div className="px-3 space-y-1">
                    {groupedNavigation?.map((group) => {
                      const isOpen = openGroups?.[group?.groupLabel] === true;
                      const hasActiveItem = group?.items?.some((item) => isActive(item?.path));
                      return (
                        <div key={group?.groupLabel}>
                          <button
                            onClick={() => toggleGroup(group?.groupLabel)}
                            className="w-full flex items-center justify-center px-3 py-2 mb-0.5 rounded-md hover:bg-muted transition-smooth group relative"
                            title={group?.groupLabel}
                          >
                            <Icon name={group?.icon} size={18} color={hasActiveItem ? 'var(--color-primary)' : '#0e7572'} />
                            <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50" style={{ color: '#0e7572' }}>
                              {group?.groupLabel}
                            </div>
                          </button>
                          {(isOpen || isCollapsed) && (
                            <div className="space-y-0.5 mb-2">
                              {group?.items?.map((item) => (
                                <Link
                                  key={item?.path}
                                  to={item?.path}
                                  data-tour={tourAttrMap?.[item?.path] || undefined}
                                  className={`flex items-center justify-center gap-3 px-3 py-2.5 rounded-md font-medium text-sm transition-smooth group relative ${isActive(item?.path) ? 'bg-primary/10 text-primary' : 'text-[#6B7280] hover:text-foreground hover:bg-muted'}`}
                                  title={item?.label}
                                >
                                  <Icon name={item?.icon} size={20} color={isActive(item?.path) ? 'var(--color-primary)' : '#107570'} />
                                  <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                                    {item?.label}
                                  </div>
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>)
          )}

          {/* Divider */}
          <div className="my-4 mx-3 border-t border-border" />

          {/* Bottom / remaining links */}
          <div className="space-y-0.5 px-3">
            {bottomNavItems?.filter(item => !(impersonationData && item?.path === '/super-admin-login'))?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                data-tour={tourAttrMap?.[item?.path] || undefined}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-md font-medium text-sm
                  transition-smooth group relative
                  ${isActive(item?.path)
                    ? 'text-gray-700 hover:bg-muted' : 'text-[#6B7280] hover:text-foreground hover:bg-muted'
                  }
                  ${isCollapsed ? 'justify-center' : ''}
                `}
                title={isCollapsed ? item?.label : ''}
              >
                <Icon
                  name={item?.icon}
                  size={20}
                  color={isActive(item?.path) ? '#4B5563' : '#107570'}
                />
                {!isCollapsed && <span className="flex-1">{item?.label}</span>}
                {isCollapsed && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                    {item?.label}
                  </div>
                )}
              </Link>
            ))}
          </div>

          {/* Plan Usage Section */}
          {!isCollapsed && (
            <div className="mt-6 px-3">
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="p-3 border-b border-border">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-heading font-semibold text-foreground">
                      Plan Usage
                    </h3>
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
                      <Icon name="Zap" size={12} />
                      <span>{planData?.planName}</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {planData?.billingCycle}
                  </p>
                </div>
                <div className="p-3 space-y-4">
                  {planData?.limits?.map((limit) => {
                    const percentage = getUsagePercentage(limit?.used, limit?.limit);
                    return (
                      <div key={limit?.id}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-1.5">
                            <Icon 
                              name={limit?.icon} 
                              size={14} 
                              color="var(--color-muted-foreground)" 
                            />
                            <span className="text-xs font-medium text-foreground">
                              {limit?.label}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {limit?.used?.toLocaleString()} / {limit?.limit?.toLocaleString()}
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-smooth ${getUsageColor(percentage)}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        {percentage >= 75 && (
                          <p className="text-xs text-warning mt-1">
                            {percentage >= 90 ? 'Approaching limit' : 'Consider upgrading'}
                          </p>
                        )}
                      </div>
                    );
                  })}

                  <div className="pt-3 border-t border-border">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-xs font-medium text-foreground mb-0.5">
                          Next billing
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {planData?.nextBillingDate}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-medium text-foreground mb-0.5">
                          ${planData?.monthlyPrice}/mo
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Monthly
                        </p>
                      </div>
                    </div>

                    <Link to="/account-settings">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="ArrowUpRight"
                        iconPosition="right"
                        fullWidth
                      >
                        Manage Plan
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </nav>
      </aside>
    </SidebarContext.Provider>
  );
};

export default Sidebar;
